import { useState } from 'react';
import { usePaytoQueryStore } from '@store/payto-query';
import { paytoRequestSingleMandate } from '@helper/service/payment';

interface PaytoQuerySearchProps {
  onSearchComplete?: () => void;
}

export default function PaytoQuerySearch({ onSearchComplete }: PaytoQuerySearchProps) {
  const [searchType, setSearchType] = useState<'end_to_end_identification' | 'mandate_identification'>('end_to_end_identification');
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [narrativeQuery, setNarrative] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      alert('Please enter a search value');
      return;
    }

    setIsLoading(true);
    try {
      const res = await paytoRequestSingleMandate(searchType, searchValue.trim());
      setSearchResult(res.data);
      
      if (onSearchComplete) {
        onSearchComplete();
      }
    } catch (err) {
      alert('Search error: ' + (err?.message || String(err)));
      setSearchResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMandateQuery = async () => {
    if (!searchResult || !narrativeQuery.trim()) {
      alert('Please provide a narrative');
      return;
    }

    setIsSubmitting(true);
    try {
      const queryData = {
        ...searchResult,
        narrativeQuery: narrativeQuery.trim()
      };

      console.log('Mandate Query Data:', JSON.stringify(queryData, null, 2));
      
      const res = await usePaytoQueryStore.getState().addQuery(queryData, 'mandate');
      alert('Mandate Query Response: ' + JSON.stringify(res));
    } catch (err) {
      alert('Error: ' + (err?.message || String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentQuery = async () => {
    if (!searchResult || !narrativeQuery.trim()) {
      alert('Please provide a narrative');
      return;
    }

    setIsSubmitting(true);
    try {
      const queryData = {
        ...searchResult,
        narrativeQuery: narrativeQuery.trim()
      };
      console.log('Payment Query Data:', JSON.stringify(queryData, null, 2));
      const res = await usePaytoQueryStore.getState().addQuery(queryData, 'payment');
      alert('Payment Query Response: ' + JSON.stringify(res));
    } catch (err) {
      alert('Error: ' + (err?.message || String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tw-mb-4 tw-p-4 tw-border tw-rounded tw-bg-gray-50">
      <h3 className="tw-text-lg tw-font-medium tw-mb-4">Search PayTo Records</h3>
      
      <div className="tw-flex tw-space-x-4 tw-items-end tw-mb-4">
        <div className="tw-flex-1">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Search By
          </label>
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as 'end_to_end_identification' | 'mandate_identification')}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
          >
            <option value="end_to_end_identification">End to End ID</option>
            <option value="mandate_identification">Mandate Identification</option>
          </select>
        </div>

        <div className="tw-flex-2">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Search Value
          </label>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={`Enter ${searchType === 'end_to_end_identification' ? 'End to End ID' : 'Mandate Identification'}`}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
          />
        </div>

        <button
          type="button"
          onClick={handleSearch}
          disabled={isLoading}
          className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded tw-hover:bg-blue-700 tw-disabled:opacity-50"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {searchResult && (
        <div className="tw-mt-6 tw-p-4 tw-border tw-rounded tw-bg-white">
          <h4 className="tw-text-md tw-font-medium tw-mb-3">Search Result Found</h4>
          <pre className="tw-text-sm tw-bg-gray-100 tw-p-3 tw-rounded tw-mb-4 tw-overflow-auto">
            {JSON.stringify(searchResult, null, 2)}
          </pre>
          
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Narrative
            </label>
            <textarea
              value={narrativeQuery}
              onChange={(e) => setNarrative(e.target.value)}
              placeholder="Enter narrative for the query..."
              rows={3}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
            />
          </div>
          
          <div className="tw-flex tw-space-x-3">
            <button
              type="button"
              onClick={handleMandateQuery}
              disabled={isSubmitting}
              className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded tw-hover:bg-blue-700 tw-disabled:opacity-50"
            >
              {isSubmitting ? 'Processing...' : 'Mandate Query'}
            </button>
            
            <button
              type="button"
              onClick={handlePaymentQuery}
              disabled={isSubmitting}
              className="tw-bg-green-600 tw-text-white tw-px-4 tw-py-2 tw-rounded tw-hover:bg-green-700 tw-disabled:opacity-50"
            >
              {isSubmitting ? 'Processing...' : 'Payment Query'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}